import { useState, useCallback, useRef } from 'react';
import { Message, APIMessage } from '../types/chat';
import { CHAT_MESSAGES } from '../constants/chatMessages';
import { useHomeAreasContext } from '../contexts/HomeAreasContext';

// Define interface for the area object structure
export interface Area {
  area_name: string;
  image_ids: string[];
  primary_image?: string | null;
  tasks: {
    summary: string;
    details: string;
    category?: string;
  }[];
}

export function useChatOperations(homeId: string | undefined) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState<boolean>(false);
  const [areas, setAreas] = useState<Area[]>([]);
  const [completedTaskIds, setCompletedTaskIds] = useState<Set<string>>(new Set());
  const [isUpdatingTask, setIsUpdatingTask] = useState<boolean>(false);
  const [taskUpdateError, setTaskUpdateError] = useState<string | null>(null);
  const lastHomeIdRef = useRef<string | null>(null);
  const [homePrimaryImage, setHomePrimaryImage] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number>(Date.now());
  const areasContext = useHomeAreasContext();

  // Basic function to toggle task completion status (client-side only)
  const toggleTaskCompletion = useCallback((taskId: string) => {
    setCompletedTaskIds(prevCompletedTasks => {
      const newCompletedTasks = new Set(prevCompletedTasks);
      if (newCompletedTasks.has(taskId)) {
        newCompletedTasks.delete(taskId);
      } else {
        newCompletedTasks.add(taskId);
      }
      return newCompletedTasks;
    });
  }, []);

  // New function to update task on the server
  const updateTask = useCallback(async (
    taskId: string, 
    areaName: string, 
    taskSummary: string, 
    updates: {
      is_completed?: boolean;
      summary?: string;
      details?: string;
      category?: string;
      notes?: string;
    }
  ) => {
    if (!homeId) {
      setTaskUpdateError('No home ID available');
      return false;
    }

    // Clear any previous errors
    setTaskUpdateError(null);
    setIsUpdatingTask(true);

    try {
      const response = await fetch(`/api/homes/${homeId}/tasks`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          areaName,
          taskSummary,
          updates
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to update task');
      }

      // If we're updating completion status, also update local state
      if (updates.is_completed !== undefined) {
        if (updates.is_completed) {
          setCompletedTaskIds(prev => new Set([...prev, taskId]));
        } else {
          setCompletedTaskIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(taskId);
            return newSet;
          });
        }
      }

      return true;
    } catch (error) {
      console.error('Error updating task:', error);
      setTaskUpdateError(error instanceof Error ? error.message : 'Failed to update task');
      return false;
    } finally {
      setIsUpdatingTask(false);
    }
  }, [homeId]);

  // Convenience function specifically for toggling completion status
  const toggleTaskCompletionWithServer = useCallback(async (
    taskId: string,
    areaName: string,
    taskSummary: string
  ) => {
    const isCurrentlyCompleted = completedTaskIds.has(taskId);
    const newCompletionStatus = !isCurrentlyCompleted;
    
    // Optimistically update UI
    toggleTaskCompletion(taskId);
    
    // Update server
    const success = await updateTask(
      taskId,
      areaName,
      taskSummary,
      { is_completed: newCompletionStatus }
    );
    
    // If server update failed, revert the UI change
    if (!success) {
      toggleTaskCompletion(taskId);
    }
    
    return success;
  }, [completedTaskIds, toggleTaskCompletion, updateTask]);

  // Function to get count of active (uncompleted) tasks
  const getActiveTaskCount = useCallback(() => {
    // Count all tasks across all areas
    const totalTasks = areas.reduce((count, area) => 
      count + (area.tasks?.length || 0), 0);
    
    // Return difference between total and completed
    return totalTasks - completedTaskIds.size;
  }, [areas, completedTaskIds]);

  // Add a general mapping helper outside the other functions
  const mapApiAreasToAreas = useCallback((apiAreas: any[]): Area[] => {
    if (!Array.isArray(apiAreas)) return [];
    
    return apiAreas.map(area => ({
      area_name: area.area_name,
      image_ids: Array.isArray(area.image_ids) ? area.image_ids : [],
      primary_image: area.primary_image || null,
      tasks: Array.isArray(area.tasks) ? area.tasks : []
    }));
  }, []);

  // Helper function to transform API messages to UI messages
  const transformAPIMessage = useCallback((msg: APIMessage): Message | null => {
    // Handle user messages
    if (msg.role === 'user') {
      const messageText = msg.content[0]?.text?.value || '';
      
      // Filter out system messages that we don't want to display
      if (messageText.startsWith("The files I just shared correspond to these images_ids:") || 
          Object.values(CHAT_MESSAGES).some(m => m === messageText) || 
          messageText === ""
        ) {
        return null;
      }
      
      return {
        name: 'You',
        message: messageText,
        justification: 'right' as const
      };
    }
    
    // Handle assistant messages
    if (msg.role === 'assistant') {
      // Process object content (parsed JSON)
      if (msg.content[0]?.type === 'object' && msg.content[0]?.object) {
        const content = msg.content[0].object;
        
        // Only return a message object if there's actual chat content to display
        if (content.chat_response && content.chat_response.trim()) {
          return {
            name: 'CribCritic.ai',
            message: content.chat_response,
            justification: 'left' as const
          };
        }
        
        // Return null if there's no chat response to display
        return null;
      }
      
      // Handle plain text content as fallback
      const textContent = msg.content[0]?.text?.value || '';
      if (textContent.trim()) {
        return {
          name: 'CribCritic.ai',
          message: textContent,
          justification: 'left'
        };
      }
      
      // Return null for empty text messages
      return null;
    }
    
    return null;
  }, []);

  // Helper function to extract areas from a message
  const extractAreasFromMessage = useCallback((msg: APIMessage): Area[] => {
    if (msg.role === 'assistant' && 
        msg.content[0]?.type === 'object' && 
        msg.content[0]?.object) {
      
      const areasSeen = msg.content[0].object.areas_seen;
      // Add this check to ensure areasSeen is an array
      if (Array.isArray(areasSeen)) {
        return mapApiAreasToAreas(areasSeen);
      }
    }
    return [];
  }, [mapApiAreasToAreas]);

  // Helper function to update areas state
  const updateAreas = useCallback((newAreas: Area[]) => {
    setAreas(prev => {
      // Create a map of existing areas by name for easy lookup
      const existingAreasMap = new Map(prev.map(area => [area.area_name, area]));
      
      // Process each new area
      newAreas.forEach(newArea => {
        const existingArea = existingAreasMap.get(newArea.area_name);
        
        if (existingArea) {
          // Merge with existing area
          const mergedImageIds = Array.from(new Set([
            ...existingArea.image_ids || [], 
            ...newArea.image_ids || []
          ]));
          
          // Merge tasks (avoiding duplicates by summary)
          const mergedTasks = [...existingArea.tasks || []];
          (newArea.tasks || []).forEach(newTask => {
            if (!mergedTasks.some(t => t.summary === newTask.summary)) {
              mergedTasks.push(newTask);
            }
          });
          
          // Update the map with merged area
          existingAreasMap.set(newArea.area_name, {
            ...existingArea,
            image_ids: mergedImageIds,
            tasks: mergedTasks,
            // Update primary_image if new data provides one, otherwise keep existing
            primary_image: newArea.primary_image !== undefined ? newArea.primary_image : existingArea.primary_image
          });
        } else {
          // This is a new area, add it to the map
          existingAreasMap.set(newArea.area_name, {
            area_name: newArea.area_name,
            image_ids: newArea.image_ids || [],
            primary_image: newArea.primary_image || null,
            tasks: newArea.tasks || []
          });
        }
      });

      // Convert map back to array
      return [...Array.from(existingAreasMap.values())];
    });
  }, []);

  // Function to load messages
  const refreshMessages = useCallback(async () => {
    if (!homeId) return;
    
    setIsLoadingMessages(true);
    try {
      console.log('ChatProvider initializing for homeId:', homeId);
      const response = await fetch('/api/chat/get_all_messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          home_id: homeId
        })
      });

      const data = await response.json();
      if (data.success && data.messages) {
        // Extract areas from messages
        const allAreas: Area[] = [];
        let latestPrimaryImage: string | null = null;
        
        // Transform API messages to UI messages
        const transformedMessages = data.messages
          .filter((msg: APIMessage) => msg.role && msg.content && msg.content.length > 0)
          .map((msg: APIMessage) => {
            // Extract areas
            const areas = extractAreasFromMessage(msg);
            allAreas.push(...areas);
            
            // Extract primary_image if present
            if (msg.role === 'assistant' && 
                msg.content[0]?.type === 'object' && 
                msg.content[0]?.object &&
                msg.content[0].object.primary_image) {
              latestPrimaryImage = msg.content[0].object.primary_image;
            }
            
            // Transform message
            return transformAPIMessage(msg);
          })
          .filter(Boolean) as Message[];
          
        setMessages(transformedMessages);
        updateAreas(allAreas);
        
        // Update the home's primary image with the latest one found
        if (latestPrimaryImage) {
          setHomePrimaryImage(latestPrimaryImage);
        }
        
        // Update timestamp to signal new data
        setLastUpdated(Date.now());
        
        if (areasContext) {
          areasContext.fetchAreas();
        }
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [homeId, transformAPIMessage, extractAreasFromMessage, updateAreas]);

  // Function to send a message
  const sendMessage = useCallback(async (messageText: string) => {
    if (!homeId) return;
    
    // Create a simulated API message to run through our existing filter logic
    const simulatedUserApiMessage: APIMessage = {
      role: 'user',
      content: [{ 
        type: 'text', 
        text: { 
          value: messageText,
          annotations: []
        } 
      }]
    };
    
    // Check if this message should be displayed
    const userMessageForUI = transformAPIMessage(simulatedUserApiMessage);
    
    // If it should be displayed, add to UI
    if (userMessageForUI) {
      setMessages(prev => [...prev, userMessageForUI]);
    }
    
    // Regardless of display filtering, send to API
    setIsLoading(true);
    try {
      const response = await fetch('/api/chat/inference', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          home_id: homeId,
          msg: messageText
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Store the home's primary image if available
        if (result.primary_image) {
          setHomePrimaryImage(result.primary_image);
        }
        
        // Handle areas_seen in the new format first
        if (result.areas_seen && Array.isArray(result.areas_seen)) {
          updateAreas(mapApiAreasToAreas(result.areas_seen));
        }
        
        // Only add the AI response to UI if there's a message
        if (result.message) {
          const aiMessage: Message = {
            name: 'CribCritic.ai',
            message: result.message,
            justification: 'left'
          };
          
          setMessages(prev => [...prev, aiMessage]);
        }
        
        // Update timestamp to signal new data
        setLastUpdated(Date.now());
        
        if (areasContext) {
          areasContext.fetchAreas();
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  }, [homeId, updateAreas, transformAPIMessage, mapApiAreasToAreas]);

  return {
    messages,
    isLoading,
    isLoadingMessages,
    areas,
    lastHomeIdRef,
    sendMessage,
    refreshMessages,
    completedTaskIds,
    toggleTaskCompletion,
    updateTask,
    toggleTaskCompletionWithServer,
    isUpdatingTask,
    taskUpdateError,
    getActiveTaskCount,
    homePrimaryImage,
    clearTaskUpdateError: () => setTaskUpdateError(null),
    lastUpdated
  };
}